import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import GridOnIcon from '@mui/icons-material/GridOn';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SettingsIcon from '@mui/icons-material/Settings';
import { format } from 'date-fns';
import { BRAND, avatarColor, initialsOf } from '../../theme';
import { monthLabel, toDate } from '../../utils/dates';
import { formatCurrency } from '../../utils/money';
import {
  ColumnVisibility,
  DriverDirectory,
  OPTIONAL_COLUMNS,
  OptionalColumn,
  SORT_OPTIONS,
  SortKey,
  SortState,
  STATUS_LABELS,
  driverInfoFor
} from './types';

interface InvoiceTableProps {
  /** Page of rows to render */
  rows: any[];
  /** Every row matching the current filters, across all pages */
  filteredCount: number;
  selectedIds: string[];
  onToggleRow: (invoice: any) => void;
  onTogglePage: () => void;
  onSelectAllFiltered: () => void;
  onClearSelection: () => void;
  selectionEnabled: boolean;
  isRowSelectable: (invoice: any) => boolean;
  sort: SortState;
  onSortChange: (sort: SortState) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  columns: ColumnVisibility;
  onColumnsChange: (columns: ColumnVisibility) => void;
  groupByMonth: boolean;
  onGroupByMonthChange: (groupByMonth: boolean) => void;
  /** Count and total per month across every filtered invoice, not just this page */
  monthSummaries: Record<string, { count: number; total: number }>;
  driverDirectory: DriverDirectory;
  showDriver: boolean;
  showTotals: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onView: (invoice: any) => void;
  onEdit: (invoice: any) => void;
  onDownloadExcel: (invoice: any) => void;
  onDownloadPdf: (invoice: any) => void;
  onDelete: (invoice: any) => void;
}

const formatDate = (value: any) => {
  const date = toDate(value);
  return date ? format(date, 'MM/dd/yyyy h:mm a') : 'N/A';
};

interface MonthGroup {
  month: string | null;
  rows: any[];
  total: number;
}

/**
 * Splits the page into runs of the same month. Only meaningful while the table
 * is sorted by date — any other order would scatter each month across the page.
 */
const groupRowsByMonth = (rows: any[], enabled: boolean): MonthGroup[] => {
  if (!enabled) return [{ month: null, rows, total: 0 }];

  return rows.reduce<MonthGroup[]>((groups, invoice) => {
    const month = monthLabel(invoice.createdAt);
    const current = groups[groups.length - 1];

    if (current && current.month === month) {
      current.rows.push(invoice);
      current.total += invoice.totalCharges || 0;
    } else {
      groups.push({ month, rows: [invoice], total: invoice.totalCharges || 0 });
    }

    return groups;
  }, []);
};

const statusStyles = (status: string) => {
  switch (status) {
    case 'PAID':
      return { backgroundColor: BRAND.paid.background, color: BRAND.paid.text };
    case 'pay_to_driver':
      return { backgroundColor: BRAND.payToDriver.background, color: BRAND.payToDriver.text };
    default:
      return { backgroundColor: BRAND.pending.background, color: BRAND.pending.text };
  }
};

const InvoiceTable: React.FC<InvoiceTableProps> = ({
  rows,
  filteredCount,
  selectedIds,
  onToggleRow,
  onTogglePage,
  onSelectAllFiltered,
  onClearSelection,
  selectionEnabled,
  isRowSelectable,
  sort,
  onSortChange,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  columns,
  onColumnsChange,
  groupByMonth,
  onGroupByMonthChange,
  monthSummaries,
  driverDirectory,
  showDriver,
  showTotals,
  canEdit,
  canDelete,
  onView,
  onEdit,
  onDownloadExcel,
  onDownloadPdf,
  onDelete
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuInvoice, setMenuInvoice] = useState<any>(null);
  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);

  const selectableRows = rows.filter(isRowSelectable);
  const selectedOnPage = selectableRows.filter(row => selectedIds.includes(row.id)).length;
  const allPageSelected = selectableRows.length > 0 && selectedOnPage === selectableRows.length;

  const activeSortLabel =
    SORT_OPTIONS.find(option => option.key === sort.key && option.direction === sort.direction)
      ?.label || SORT_OPTIONS[0].label;

  const handleColumnSort = (key: SortKey) => {
    if (sort.key === key) {
      onSortChange({ key, direction: sort.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      onSortChange({ key, direction: key === 'createdAt' ? 'desc' : 'asc' });
    }
  };

  const openRowMenu = (event: React.MouseEvent<HTMLElement>, invoice: any) => {
    setMenuInvoice(invoice);
    setMenuAnchor(event.currentTarget);
  };

  const closeRowMenu = () => {
    setMenuAnchor(null);
    setMenuInvoice(null);
  };

  const runRowAction = (action: (invoice: any) => void) => {
    const invoice = menuInvoice;
    closeRowMenu();
    if (invoice) action(invoice);
  };

  // Month runs only line up when the page is ordered by date.
  const grouped = groupByMonth && sort.key === 'createdAt';
  const monthGroups = groupRowsByMonth(rows, grouped);

  const columnCount =
    1 +
    (selectionEnabled ? 1 : 0) +
    Object.keys(OPTIONAL_COLUMNS).filter(key => columns[key as OptionalColumn]).length +
    1 + // created
    (showDriver ? 1 : 0) +
    (showTotals ? 1 : 0) +
    2; // status + actions

  // One invoice row, reused inside each month group.
  const renderRow = (invoice: any) => {
    const selected = selectedIds.includes(invoice.id);
    const selectable = isRowSelectable(invoice);
    const status = invoice.status || 'pending';
    const driver = driverInfoFor(driverDirectory, invoice.driver);

    return (
      <TableRow
        key={invoice.id}
        hover
        selected={selected}
        sx={{ '&.Mui-selected': { backgroundColor: 'rgba(21,101,216,0.06)' } }}
      >
        {selectionEnabled && (
          <TableCell padding="checkbox">
            <Tooltip
              title={
                selectable
                  ? ''
                  : invoice.driver
                    ? 'Only pending invoices can be paid out'
                    : 'This invoice has no driver assigned'
              }
            >
              <span>
                <Checkbox
                  checked={selected}
                  disabled={!selectable}
                  onChange={() => onToggleRow(invoice)}
                />
              </span>
            </Tooltip>
          </TableCell>
        )}
        <TableCell>
          <Typography
            component="span"
            sx={{ color: 'primary.main', fontWeight: 600, fontSize: 14 }}
          >
            {invoice.invoiceNumber || 'N/A'}
          </Typography>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(invoice.createdAt)}</TableCell>
        {columns.orderNumber && <TableCell>{invoice.orderNumber || 'N/A'}</TableCell>}
        {columns.loadNumber && <TableCell>{invoice.loadNumber || 'N/A'}</TableCell>}
        {columns.broker && <TableCell>{invoice.broker || 'N/A'}</TableCell>}
        {columns.terminal && <TableCell>{invoice.terminal || 'N/A'}</TableCell>}
        {columns.destination && (
          <TableCell sx={{ maxWidth: 220, whiteSpace: 'normal' }}>
            {invoice.destination || 'N/A'}
          </TableCell>
        )}
        {showDriver && (
          <TableCell>
            <Tooltip title={driver.name}>
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  fontSize: 12,
                  fontWeight: 700,
                  bgcolor: `${avatarColor(driver.dogtag || 'none')}22`,
                  color: avatarColor(driver.dogtag || 'none')
                }}
              >
                {initialsOf(driver.name)}
              </Avatar>
            </Tooltip>
          </TableCell>
        )}
        {showTotals && (
          <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {formatCurrency(invoice.totalCharges)}
          </TableCell>
        )}
        <TableCell>
          <Chip
            size="small"
            label={STATUS_LABELS[status] || status}
            sx={statusStyles(status)}
          />
        </TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Tooltip title="View details">
              <IconButton size="small" color="primary" onClick={() => onView(invoice)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canEdit && (
              <Tooltip title="Edit invoice">
                <IconButton size="small" color="primary" onClick={() => onEdit(invoice)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="More options">
              <IconButton size="small" onClick={(e) => openRowMenu(e, invoice)}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {/* Selection / sort bar */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          px: 2,
          py: 1.5,
          backgroundColor: selectedIds.length > 0 ? 'rgba(21,101,216,0.06)' : 'transparent',
          borderBottom: `1px solid ${BRAND.border}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 34 }}>
          {selectionEnabled && selectedIds.length > 0 ? (
            <>
              <Checkbox checked indeterminate={!allPageSelected} onChange={onClearSelection} />
              <Typography variant="body2" fontWeight={600}>
                {selectedIds.length} invoice{selectedIds.length === 1 ? '' : 's'} selected
              </Typography>
              {selectedIds.length < filteredCount && (
                <Button size="small" onClick={onSelectAllFiltered}>
                  Select all {filteredCount} invoices
                </Button>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {filteredCount} invoice{filteredCount === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Sort by
          </Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={activeSortLabel}
              onChange={(e) => {
                const option = SORT_OPTIONS.find(item => item.label === e.target.value);
                if (option) onSortChange({ key: option.key, direction: option.direction });
              }}
            >
              {SORT_OPTIONS.map(option => (
                <MenuItem key={option.label} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Choose columns">
            <IconButton onClick={(e) => setColumnsAnchor(e.currentTarget)}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {selectionEnabled && (
                <TableCell padding="checkbox">
                  <Tooltip
                    title={
                      selectableRows.length === 0
                        ? 'No invoices on this page can be paid out'
                        : 'Select every invoice on this page'
                    }
                  >
                    <span>
                      <Checkbox
                        checked={allPageSelected}
                        indeterminate={selectedOnPage > 0 && !allPageSelected}
                        disabled={selectableRows.length === 0}
                        onChange={onTogglePage}
                      />
                    </span>
                  </Tooltip>
                </TableCell>
              )}
              <TableCell>
                <TableSortLabel
                  active={sort.key === 'invoiceNumber'}
                  direction={sort.key === 'invoiceNumber' ? sort.direction : 'asc'}
                  onClick={() => handleColumnSort('invoiceNumber')}
                >
                  Invoice #
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sort.key === 'createdAt'}
                  direction={sort.key === 'createdAt' ? sort.direction : 'desc'}
                  onClick={() => handleColumnSort('createdAt')}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              {columns.orderNumber && <TableCell>Order #</TableCell>}
              {columns.loadNumber && <TableCell>Load #</TableCell>}
              {columns.broker && <TableCell>Broker</TableCell>}
              {columns.terminal && <TableCell>Terminal</TableCell>}
              {columns.destination && <TableCell>Destination</TableCell>}
              {showDriver && <TableCell>Driver</TableCell>}
              {showTotals && (
                <TableCell>
                  <TableSortLabel
                    active={sort.key === 'totalCharges'}
                    direction={sort.key === 'totalCharges' ? sort.direction : 'desc'}
                    onClick={() => handleColumnSort('totalCharges')}
                  >
                    Total Charges
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">
                    No invoices match the current filters
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {monthGroups.map((group) => {
              // The month's own totals, with a note when the page shows only part of it.
              const summary = group.month
                ? monthSummaries[group.month] || { count: group.rows.length, total: group.total }
                : { count: 0, total: 0 };
              const partial = group.month ? group.rows.length < summary.count : false;

              return (
              <React.Fragment key={group.month || 'all'}>
                {group.month && (
                  <TableRow>
                    <TableCell
                      colSpan={columnCount}
                      sx={{
                        py: 1,
                        backgroundColor: '#EFF4FE',
                        borderBottom: `1px solid ${BRAND.border}`
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ color: BRAND.navy, letterSpacing: 0.4 }}
                        >
                          {group.month}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {partial ? `${group.rows.length} of ` : ''}
                          {summary.count} invoice{summary.count === 1 ? '' : 's'}
                          {showTotals ? ` · ${formatCurrency(summary.total)}` : ''}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {group.rows.map((invoice) => renderRow(invoice))}
              </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredCount}
        page={page}
        onPageChange={(_, nextPage) => onPageChange(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="Show"
      />

      {/* Row overflow menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeRowMenu}>
        <MenuItem onClick={() => runRowAction(onDownloadExcel)}>
          <ListItemIcon>
            <GridOnIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download invoice (Excel)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => runRowAction(onDownloadPdf)}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download invoice (PDF)</ListItemText>
        </MenuItem>
        {canDelete && <Divider />}
        {canDelete && (
          <MenuItem onClick={() => runRowAction(onDelete)} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete invoice</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Table display menu */}
      <Menu
        anchorEl={columnsAnchor}
        open={Boolean(columnsAnchor)}
        onClose={() => setColumnsAnchor(null)}
      >
        <Typography variant="caption" sx={{ px: 2, color: 'text.secondary' }}>
          Grouping
        </Typography>
        <Tooltip
          title={
            sort.key === 'createdAt'
              ? ''
              : 'Sort by Created to group invoices by month'
          }
        >
          <span>
            <MenuItem
              disabled={sort.key !== 'createdAt'}
              onClick={() => onGroupByMonthChange(!groupByMonth)}
            >
              <Checkbox size="small" checked={grouped} sx={{ mr: 1 }} />
              Group by month
            </MenuItem>
          </span>
        </Tooltip>
        <Divider />
        <Typography variant="caption" sx={{ px: 2, color: 'text.secondary' }}>
          Columns
        </Typography>
        {(Object.keys(OPTIONAL_COLUMNS) as OptionalColumn[]).map((key) => (
          <MenuItem
            key={key}
            onClick={() => onColumnsChange({ ...columns, [key]: !columns[key] })}
          >
            <Checkbox size="small" checked={columns[key]} sx={{ mr: 1 }} />
            {OPTIONAL_COLUMNS[key]}
          </MenuItem>
        ))}
      </Menu>
    </Paper>
  );
};

export default InvoiceTable;
